import { useCallback } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { showWarningNotification } from 'src/components/widgets/notificationToaster/notificationToaster';
import { useAIStudioCreditsDialog } from '../components/dialogs/aiStudioNoCreditsDialog';
import { ServicesWithCredits, useGenerationPreflightFirstPackMutation } from './__generated__/useGenerationPreflightFirstPackMutation.graphql';
import { useGenerationPreflightTrialStatusQuery } from './__generated__/useGenerationPreflightTrialStatusQuery.graphql';
import { ServiceAvailabilityResult, useGenerationPreflightAvailabilityQuery } from './__generated__/useGenerationPreflightAvailabilityQuery.graphql';

/**
 * All AI Studio tools draw from a single shared credit pool, exposed through the
 * AI_STUDIO service. The first free credit pack is therefore always granted against this pool.
 */
const AI_STUDIO_POOL_SERVICE: ServicesWithCredits = 'AI_STUDIO';

/** Addon name as stored on the subscription (see subscription.addons). */
const AI_STUDIO_ADDON_NAME = 'AI_STUDIO';

export interface PreflightOptions {
    /** Fired synchronously when the AI_STUDIO addon is missing and an activation flow has been triggered. Useful to show optimistic UI. */
    onAddonActivationStarted?: () => void;
    /** When false, the one-time free first pack granted on addon activation is created without auto-refill. Defaults to true. */
    autoRefill?: boolean;
    /**
     * Overrides the credit-purchase dialog shown on NO_CREDITS. When provided it is invoked instead of the shared
     * AI Studio dialog, letting a specific flow (e.g. product-setup side images) present its own no-credits modal.
     */
    onNoCredits?: () => void;
}

export interface UseGenerationPreflightReturn {
    /**
     * Resolves the seller's AI Studio availability via serviceAvailabilityStatus and gates onReady on it:
     * - AVAILABLE: invoke onReady immediately.
     * - ADDON_NOT_ACTIVE: trigger the activation flow, granting the one-time free first pack, then invoke
     *   onReady on successful activation.
     * - NO_CREDITS: open the credit-purchase dialog; onReady is not invoked.
     * - SHOPIFY_DEV_STORE_NOT_ALLOWED: show a warning toast; onReady is not invoked.
     * Any other unavailability reason falls through to onReady so the start mutation surfaces the precise error.
     */
    runWithPreflight: (onReady: () => void | Promise<void>, options?: PreflightOptions) => Promise<void>;
}

export function useGenerationPreflight(): UseGenerationPreflightReturn {
    const relayEnvironment = useRelayEnvironment();
    const { activateAddonForSeller, updateSubscription } = useSubscriptionData();
    const { showNoCreditsDialog } = useAIStudioCreditsDialog();

    const [grantFirstFreePack] = useMutation<useGenerationPreflightFirstPackMutation>(graphql`
        mutation useGenerationPreflightFirstPackMutation($input: ServiceRechargeCreditsInput!) {
            buyServiceCredits(input: $input) {
                result
                errorMessage
            }
        }
    `);

    // Queries the backend for the authoritative availability status of the AI Studio service for the
    // current subscription. Returns null when the request fails so callers can fall back gracefully.
    const fetchAvailabilityStatus = useCallback(async (): Promise<ServiceAvailabilityResult | null> => {
        try {
            const data = await fetchQuery<useGenerationPreflightAvailabilityQuery>(
                relayEnvironment,
                graphql`
                    query useGenerationPreflightAvailabilityQuery($serviceName: ServicesWithCredits!) {
                        serviceAvailabilityStatus(serviceWithCredits: $serviceName)
                    }
                `,
                { serviceName: AI_STUDIO_POOL_SERVICE },
                { fetchPolicy: 'network-only' }
            ).toPromise();
            return data?.serviceAvailabilityStatus ?? null;
        } catch (error) {
            console.error('Failed to check AI Studio service availability:', error);
            return null;
        }
    }, [relayEnvironment]);

    // Returns true when the seller has never received trial credits and has never bought credits for the
    // AI Studio pool — i.e. they are entitled to the one-time free first package.
    const isEligibleForFirstFreePack = useCallback(async (): Promise<boolean> => {
        try {
            const data = await fetchQuery<useGenerationPreflightTrialStatusQuery>(
                relayEnvironment,
                graphql`
                    query useGenerationPreflightTrialStatusQuery($serviceName: ServicesWithCredits!) {
                        serviceCredit(serviceName: $serviceName) {
                            trialCreditsUsed
                            hasSellerPurchasedCredits
                        }
                    }
                `,
                { serviceName: AI_STUDIO_POOL_SERVICE },
                { fetchPolicy: 'network-only' }
            ).toPromise();
            const credit = data?.serviceCredit;
            // If serviceCredit is null the seller has no row yet, which also means they're eligible.
            if (!credit) return true;
            return !credit.trialCreditsUsed && !credit.hasSellerPurchasedCredits;
        } catch (error) {
            console.error('Failed to check AI Studio trial credit status:', error);
            return false;
        }
    }, [relayEnvironment]);

    const claimFirstFreePack = useCallback(
        (autoRefill: boolean): Promise<void> => {
            return new Promise<void>((resolve) => {
                grantFirstFreePack({
                    variables: {
                        input: {
                            service: AI_STUDIO_POOL_SERVICE,
                            shopifyChargeID: null,
                            credits: 0,
                            isForTrial: true,
                            autoRefill,
                            currencyCode: 'USD',
                            returnUrlFromApproval: null
                        }
                    },
                    // The free first pack is a best-effort bonus on top of addon activation; failures
                    // must not block the user (the started task will surface NO_CREDITS instead).
                    onCompleted: () => resolve(),
                    onError: (error) => {
                        console.warn('Failed to grant AI Studio first free credit pack:', error);
                        resolve();
                    }
                });
            });
        },
        [grantFirstFreePack]
    );

    // Activates the AI_STUDIO addon, grants the one-time free first pack (when the seller is still
    // eligible, so re-activation flows don't grant it twice), refreshes the cache, then runs onReady.
    const activateAddonAndRun = useCallback(
        (onReady: () => void | Promise<void>, options: PreflightOptions) => {
            options.onAddonActivationStarted?.();
            activateAddonForSeller(
                AI_STUDIO_ADDON_NAME,
                'USD',
                async () => {
                    if (await isEligibleForFirstFreePack()) {
                        await claimFirstFreePack(options.autoRefill ?? true);
                    }
                    // Refresh the Relay cache so subsequent checks see the activated addon and new credits.
                    updateSubscription();
                    await onReady();
                },
                false
            );
        },
        [activateAddonForSeller, isEligibleForFirstFreePack, claimFirstFreePack, updateSubscription]
    );

    const runWithPreflight = useCallback(
        async (onReady: () => void | Promise<void>, options: PreflightOptions = {}) => {
            const status = await fetchAvailabilityStatus();

            switch (status) {
                case 'ADDON_NOT_ACTIVE':
                    activateAddonAndRun(onReady, options);
                    return;

                case 'NO_CREDITS':
                    if (options.onNoCredits) options.onNoCredits();
                    else showNoCreditsDialog();
                    return;

                case 'SHOPIFY_DEV_STORE_NOT_ALLOWED':
                    showWarningNotification({
                        message: T._('Your account is currently a development store, so AI Agents Studio is not available.', 'Admin')
                    });
                    return;

                // AVAILABLE and any other unavailability reason fall through to the generation, letting the
                // start mutation surface the precise error if the seller still cannot proceed.
                default:
                    await onReady();
                    return;
            }
        },
        [fetchAvailabilityStatus, activateAddonAndRun, showNoCreditsDialog]
    );

    return { runWithPreflight };
}
